import { Alert, Box, Button, TextField } from "@mui/material";
import { QRCodeSVG } from "qrcode.react";
import React, { useEffect, useState } from "react";
import { useTranslate } from "react-admin";

import { AdminMfaResponse, AdminMfaStatus } from "../generated/graphql";
import { space } from "../theme/designTokens";
import { AuthFormHeader } from "./AuthFormHeader";

type AdminMfaFormProps = {
  response: AdminMfaResponse | null;
  loading: boolean;
  error: string | null;
  isReplacement: boolean;
  resetCodeVersion: number;
  onSubmit: (code: string, recovery: boolean) => void;
  onBack: () => void;
};

export const AdminMfaForm: React.FC<AdminMfaFormProps> = ({
  response,
  loading,
  error,
  isReplacement,
  resetCodeVersion,
  onSubmit,
  onBack,
}) => {
  const translate = useTranslate();
  const [code, setCode] = useState("");
  const [recovery, setRecovery] = useState(false);
  const isEnrollment = response?.status === AdminMfaStatus.EnrollmentRequired;

  useEffect(() => {
    setCode("");
    if (response?.status === AdminMfaStatus.EnrollmentRequired) {
      setRecovery(false);
    }
  }, [resetCodeVersion, response?.challengeToken, response?.status]);

  if (!response) {
    return (
      <>
        <AuthFormHeader title={translate("mfa.title")} description={translate("mfa.restartDescription")} />
        {error && (
          <Alert severity="warning" role="alert">
            {error}
          </Alert>
        )}
        <Button fullWidth variant="contained" onClick={onBack} sx={{ marginTop: `${space.xl}px` }}>
          {translate("mfa.restart")}
        </Button>
      </>
    );
  }

  return (
    <>
      <AuthFormHeader
        title={translate("mfa.title")}
        description={translate(isEnrollment ? "mfa.setupDescription" : "mfa.verificationDescription")}
      />

      {error && (
        <Alert severity="error" role="alert" sx={{ marginBottom: `${space.sm}px` }}>
          {error}
        </Alert>
      )}

      {isEnrollment && (
        <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", gap: space.sm }}>
          {isReplacement && (
            <Alert severity="info" role="alert" sx={{ alignSelf: "stretch" }}>
              {translate("mfa.replacementSetupDescription")}
            </Alert>
          )}
          {response.otpauthUri && (
            <QRCodeSVG value={response.otpauthUri} size={220} aria-label={translate("mfa.qrCode")} />
          )}
          {response.secret && (
            <Box sx={{ alignSelf: "stretch" }}>
              <strong>{translate("mfa.manualKey")}</strong>
              <Box
                component="code"
                data-testid="admin-mfa-secret"
                sx={{ display: "block", overflowWrap: "anywhere", userSelect: "all" }}
              >
                {response.secret}
              </Box>
            </Box>
          )}
        </Box>
      )}

      <Box
        component="form"
        onSubmit={event => {
          event.preventDefault();
          onSubmit(code, recovery);
        }}
      >
        <TextField
          fullWidth
          label={translate(recovery ? "mfa.recoveryCode" : "mfa.code")}
          type="text"
          name="mfaCode"
          margin="normal"
          autoComplete={recovery ? "off" : "one-time-code"}
          inputProps={{ inputMode: recovery ? "text" : "numeric", maxLength: recovery ? 128 : 6 }}
          value={code}
          onChange={event => setCode(event.target.value)}
          disabled={loading}
          required
          autoFocus
        />

        {!isEnrollment && (
          <Button
            type="button"
            onClick={() => {
              setRecovery(value => !value);
              setCode("");
            }}
            disabled={loading}
          >
            {translate(recovery ? "mfa.useAuthenticatorCode" : "mfa.useRecoveryCode")}
          </Button>
        )}

        <Button
          type="submit"
          fullWidth
          variant="contained"
          color="primary"
          disabled={loading || code.length === 0}
          sx={{ marginTop: `${space.xl}px` }}
        >
          {loading ? translate("mfa.verifying") : translate("mfa.submit")}
        </Button>

        <Button type="button" fullWidth onClick={onBack} disabled={loading} sx={{ marginTop: `${space.sm}px` }}>
          {translate("mfa.back")}
        </Button>
      </Box>
    </>
  );
};
